/**
 * License Mode Types
 *
 * SQL Studio operates in two modes:
 * - 'personal': Free tier for personal/non-commercial use
 * - 'pro': Paid tier with advanced features (session management, full query history)
 */

export type LicenseMode = 'personal' | 'pro';

/**
 * Pro-only feature identifiers.
 * Used for feature gating with `canUseFeature()` and `ProFeatureGate`.
 */
export type ProFeature =
  | 'session-management'      // Multiple sessions, switching, naming
  | 'full-query-history'      // Full history vs last 5 closed tabs
  | 'ai-workspace'            // AI workspace sync (schema export, file sync, context files)
  | 'schema-advanced-layout'  // Advanced layout options for Schema Overview
  | 'schema-export-formats'   // Additional export formats (SVG, PDF, DOT)
  | 'impact-analysis'         // Show impact analysis for schema objects
  | 'schema-annotations'      // Custom notes/labels on schema objects
  | 'schema-comparison'       // Compare schemas between databases
  | 'schema-overview-full-selection'  // Pick and choose objects in large DB overview
  | 'ai-workspace-full-selection'     // Pick and choose objects in large DB AI workspace export
  | 'data-compare-full'       // Full comparison on tables > 10k rows
  | 'schema-compare-full'     // Full schema compare when either schema > 200 objects
  | 'database-blueprint-large'     // Blueprint folders for databases > 200 objects
  | 'backup-restore-full'     // Backup/restore databases larger than 1 GB
  | 'clone-database-full'     // Clone databases larger than 1 GB with data
  | 'clone-table-full'        // Clone tables with more than 100k rows with data
  | 'export-full'             // Export results/data with more than 100k rows
  | 'dashboard-html-snapshot' // Export a dashboard as an interactive HTML snapshot (S15)
  | 'data-import';            // Import files with more than 100k rows

/**
 * Pro feature metadata for UI display.
 */
export interface ProFeatureInfo {
  name: string;
  description: string;
}

/**
 * Registry of Pro features with their display information.
 */
export const PRO_FEATURES: Record<ProFeature, ProFeatureInfo> = {
  'session-management': {
    name: 'Session Management',
    description: 'Create, switch between, and manage multiple named sessions',
  },
  'full-query-history': {
    name: 'Full Query History',
    description: 'Access complete history of all closed tabs per session',
  },
  'ai-workspace': {
    name: 'AI Workspace',
    description: 'Schema export, file sync, and context files for AI terminal',
  },
  'schema-advanced-layout': {
    name: 'Advanced Layout Options',
    description: 'Choose different layout algorithms for Schema Overview',
  },
  'schema-export-formats': {
    name: 'Export Formats',
    description: 'Export schema diagrams as SVG, PDF, or DOT format',
  },
  'impact-analysis': {
    name: 'Impact Analysis',
    description: 'See what objects would be affected by changes',
  },
  'schema-annotations': {
    name: 'Custom Annotations',
    description: 'Add notes and labels to schema objects',
  },
  'schema-comparison': {
    name: 'Schema Comparison',
    description: 'Compare schemas between two databases',
  },
  'schema-overview-full-selection': {
    name: 'Full Object Selection',
    description: 'Pick and choose which objects to load in large database schema overviews',
  },
  'ai-workspace-full-selection': {
    name: 'AI Workspace Full Selection',
    description: 'Pick and choose which objects to export in large database AI workspaces',
  },
  'data-compare-full': {
    name: 'Full Data Compare',
    description: 'Compare tables with more than 10,000 rows without sampling',
  },
  'schema-compare-full': {
    name: 'Full Schema Compare',
    description: 'Compare schemas with more than 200 objects',
  },
  'database-blueprint-large': {
    name: 'Database Blueprint for Large Databases',
    description: 'Blueprint folders for databases with more than 200 objects',
  },
  'backup-restore-full': {
    name: 'Large Database Backup/Restore',
    description: 'Backup and restore databases larger than 1 GB',
  },
  'clone-database-full': {
    name: 'Large Database Clone',
    description: 'Clone databases larger than 1 GB with data',
  },
  'clone-table-full': {
    name: 'Large Table Clone',
    description: 'Clone tables with more than 100,000 rows with data',
  },
  'export-full': {
    name: 'Large Export',
    description: 'Export data with more than 100,000 rows',
  },
  'dashboard-html-snapshot': {
    name: 'Interactive HTML Snapshot',
    description: 'Export a dashboard as a self-contained interactive HTML file',
  },
  'data-import': {
    name: 'Large Data Import',
    description: 'Import files with more than 100,000 rows',
  },
};

/**
 * Trial state derived from trial dates.
 * - 'not-started': Trial has never been started
 * - 'active': Trial is currently active (within 14-day period)
 * - 'expired': Trial period has ended
 * - 'skipped': User chose to skip trial (Personal mode)
 */
export type TrialState = 'not-started' | 'active' | 'expired' | 'skipped';

/**
 * License state persisted across sessions.
 */
export interface LicenseState {
  mode: LicenseMode;
  // Trial-related fields
  trialStartedAt?: Date;             // When trial was started
  trialExpiresAt?: Date;             // When trial expires (14 days from start)
  trialSkipped?: boolean;            // User chose Personal mode instead of trial
  trialReminderDismissedAt?: Date;   // When user last dismissed the trial reminder
  trialCardDismissedAt?: Date;       // When user clicked "Maybe later" on the start-page card
  trialUserId?: string;              // User ID the trial belongs to (account-based trial)
  // Epoch ms of this install's first app run. Gates the trial onboarding
  // dialog to the second calendar day (day 1 shows only the passive
  // start-page card). Undefined = install predates the field → dialog
  // shows at boot (status quo for existing installs).
  firstSeenAt?: number;
}

/**
 * License context for AI workspace content generation.
 * This is passed to the context generator to produce license-aware content.
 */
export type LicenseContextMode =
  | { type: 'personal' }
  | { type: 'pro' }
  | { type: 'trial'; daysRemaining: number };

/**
 * Error returned when a Pro feature is required but the user doesn't have access.
 * IPC handlers return this error shape so the renderer can show Upgrade UX.
 *
 * This type blueprints the one defined in electron.d.ts for use in renderer code.
 */
export interface ProRequiredError {
  success: false;
  error: 'requires_pro';
  feature: ProFeature;
  featureName: string;
  featureDescription: string;
}

/**
 * Type guard to check if an IPC result is a ProRequiredError.
 * Use this to handle Pro-only operations in the renderer.
 *
 * @example
 * const result = await window.electronAPI.aiWorkspace.startWatching(sessionId);
 * if (isProRequiredError(result)) {
 *   // Show upgrade dialog
 *   openUpgradeDialog(result.featureName);
 *   return;
 * }
 */
export function isProRequiredError(result: unknown): result is ProRequiredError {
  return (
    typeof result === 'object' &&
    result !== null &&
    'success' in result &&
    (result as { success: boolean }).success === false &&
    'error' in result &&
    (result as { error: string }).error === 'requires_pro'
  );
}
